import { useMemo } from "react";
import axios, { AxiosInstance, isAxiosError } from "axios";
import { trackPromise } from "react-promise-tracker";
import ENDPOINTS from "./endpoints";
import {
  getToken,
  clearAccessToken,
  refreshAccessToken,
  setAccessToken,
} from "./tokenManager";

// Utility to get local timezone
const getTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";

// Common headers
const formDataHeaders = {
  "Content-Type": "multipart/form-data",
  timezone: getTimezone(),
};

const jsonHeaders = {
  "Content-Type": "application/json",
  timezone: getTimezone(),
};

// Axios instance factory
const createApiInstance = (
  baseURL: string,
  withCredentials = true,
  addToken = true
): AxiosInstance => {
  const instance = axios.create({ baseURL, withCredentials });

  // ===========================
  // Request Interceptor
  // ===========================
  instance.interceptors.request.use(async (config) => {
    const token = await getToken();
    if (token && addToken) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers.timezone = getTimezone();
    return config;
  });

  // ===========================
  // Response Interceptor
  // ===========================
  instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const originalRequest = error.config;
      const status = error?.response?.status;
      const message = error?.response?.data?.message;


      // -------------------------------------
      // PRIORITY 1: Handle 403 (inactive / suspended user)
      // -------------------------------------
      if (status === 403) {
        clearAccessToken();
        if (message?.includes("suspended")) {
          window.location.href = "";
        }
        return Promise.reject(error);
      }

      // -------------------------------------
      // PRIORITY 2: Handle 401 + refresh flow
      // -------------------------------------
      if (
        status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url?.includes(ENDPOINTS.AUTH.LOGIN) &&
        !originalRequest.url?.includes(ENDPOINTS.AUTH.LOGOUT)
      ) {
        originalRequest._retry = true;

        const newToken = await refreshAccessToken();
        if (newToken) {
          // Store new tokens
          setAccessToken(newToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance(originalRequest);
        } else {
          clearAccessToken();
          window.location.href = "";
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// API client wrapper
const createApiClient = (axiosInstance: AxiosInstance) => ({
  get: async (url: string, params?: object, config: object = {}) => {
    try {
      const response = await trackPromise(
        axiosInstance.get(url, {
          headers: jsonHeaders,
          params,
          ...config,
        })
      );
      return response;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        console.error("GET failed:", error.response?.data || error.message);
      } else {
        console.error("GET failed:", error);
      }
      throw error;
    }
  },

  post: async (
    url: string,
    data?: unknown,
    query: Record<string, string> | null = null
  ) => {
    try {
      const endpoint = query ? `${url}?${new URLSearchParams(query)}` : url;
      const response = await trackPromise(
        axiosInstance.post(endpoint, data, { headers: jsonHeaders })
      );
      return response;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        console.error("POST failed:", error.response?.data || error.message);
      } else {
        console.error("POST failed:", error);
      }
      throw error;
    }
  },

  put: async (url: string, data?: unknown, params: string | null = null) => {
    try {
      const endpoint = params ? `${url}/${params}` : url;
      const response = await trackPromise(
        axiosInstance.put(endpoint, data, { headers: jsonHeaders })
      );
      return response;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        console.error("PUT failed:", error.response?.data || error.message);
      } else {
        console.error("PUT failed:", error);
      }
      throw error;
    }
  },

  delete: async (
    url: string,
    params: string | null = null,
    data: unknown = null
  ) => {
    try {
      const endpoint = params ? `${url}/${params}` : url;
      const config = {
        headers: jsonHeaders,
        ...(data ? { data } : {}),
      };
      const response = await trackPromise(
        axiosInstance.delete(endpoint, config)
      );
      return response;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        console.error("DELETE failed:", error.response?.data || error.message);
      } else {
        console.error("DELETE failed:", error);
      }
      throw error;
    }
  },

  patch: async (url: string, data?: unknown, params: string | null = null) => {
    try {
      const endpoint = params ? `${url}/${params}` : url;
      const response = await trackPromise(
        axiosInstance.patch(endpoint, data, { headers: jsonHeaders })
      );
      return response;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        console.error("PATCH failed:", error.response?.data || error.message);
      } else {
        console.error("PATCH failed:", error);
      }
      throw error;
    }
  },

  postFormData: async (url: string, data?: unknown) => {
    try {
      const response = await trackPromise(
        axiosInstance.post(url, data, { headers: formDataHeaders })
      );
      return response;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        console.error(
          "POST (form-data) failed:",
          error.response?.data || error.message
        );
      } else {
        console.error("POST (form-data) failed:", error);
      }
      throw error;
    }
  },

  putFormData: async (
    url: string,
    data?: unknown,
    params: string | null = null
  ) => {
    try {
      const endpoint = params ? `${url}/${params}` : url;
      const response = await trackPromise(
        axiosInstance.put(endpoint, data, { headers: formDataHeaders })
      );
      return response;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        console.error(
          "PUT (form-data) failed:",
          error.response?.data || error.message
        );
      } else {
        console.error("PUT (form-data) failed:", error);
      }
      throw error;
    }
  },

  uploadToAzure: async (file: File, folder: string) => {
    try {
      // 1. Generate SAS URL
      const response = await trackPromise(
        axiosInstance.get(ENDPOINTS.GENERATE_UPLOAD_URL, {
          params: {
            folder,
            filename: file.name,
            contentType: file.type,
          },
          headers: jsonHeaders,
        })
      );

      const { uploadUrl, blobUrl } = response.data;

      // 2. Upload file directly to Azure Blob Storage
      await trackPromise(
        axios.put(uploadUrl, file, {
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type,
          },
        })
      );

      return blobUrl;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        console.error("Azure upload failed:", error.response?.data || error.message);
      } else {
        console.error("Azure upload failed:", error);
      }
      throw error;
    }
  },

  setRequestHeader: (key: string, value: string) => {
    axiosInstance.defaults.headers.common[key] = value;
  },
});

// Main hook
export default function useApiClient() {
  return useMemo(() => {
    const apiInstance = createApiInstance(ENDPOINTS.BASE_URL);
    return {
      api: createApiClient(apiInstance),
    };
  }, []);
}
